// Maison Jollie — product card.
// - Color swatches preview that color's image and point the card links at that variant.
// - Quick add: adds the product straight away when only one variant fits the selected
//   color, otherwise shows the available sizes on the card. Uses Horizon's cart event so
//   the cart icon and cart drawer update exactly as with the main add-to-cart button.

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADDED_TEXT = () => window.Theme?.translations?.added || 'Ajouté';

// Second and swipe images (data-mj-defer) load once the card is near the screen, or straight
// away on hover, touch or focus, instead of with the page.
const loadDeferredImages = (card) => {
  card.querySelectorAll('img[data-mj-defer]').forEach((image) => {
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.src) image.src = image.dataset.src;
    image.removeAttribute('data-mj-defer');
  });
};

const deferredImageObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            deferredImageObserver.unobserve(entry.target);
            loadDeferredImages(entry.target);
          });
        },
        { rootMargin: '200px 0px' }
      )
    : null;

// A carousel's later slides sit off to the side, so the observer above never sees them: load
// them as soon as their carousel is touched or scrolled, before they slide into view.
const loadCardsIn = (event) => {
  const container = event.target instanceof Element && event.target.closest('.mj-carousel__track, mj-product-card');
  if (!container?.querySelector('img[data-mj-defer]')) return;
  if (container.matches('mj-product-card')) return loadDeferredImages(container);
  container.querySelectorAll('mj-product-card').forEach((card) => {
    deferredImageObserver?.unobserve(card);
    loadDeferredImages(card);
  });
};
['scroll', 'touchstart', 'pointerdown'].forEach((type) =>
  document.addEventListener(type, loadCardsIn, { capture: true, passive: true })
);

class MjProductCard extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.strip = this.querySelector('.mj-card__media--swipe');
    this.strip?.addEventListener('scroll', this.#onStripScroll, { passive: true });

    if (this.querySelector('img[data-mj-defer]')) {
      if (deferredImageObserver) deferredImageObserver.observe(this);
      else loadDeferredImages(this);
      ['pointerenter', 'touchstart', 'focusin'].forEach((type) =>
        this.addEventListener(type, this.#loadImagesNow, { once: true, passive: true })
      );
    }
  }

  #loadImagesNow = () => {
    deferredImageObserver?.unobserve(this);
    loadDeferredImages(this);
  };

  // Swipeable images: keep the dots in step with the image in view
  #onStripScroll = () => {
    cancelAnimationFrame(this.stripFrame);
    this.stripFrame = requestAnimationFrame(() => {
      const index = this.strip.clientWidth ? Math.round(this.strip.scrollLeft / this.strip.clientWidth) : 0;
      this.querySelectorAll('.mj-card__dot').forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    });
  };

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    deferredImageObserver?.unobserve(this);
  }

  get #variants() {
    if (!this._variants) {
      const json = this.querySelector('[data-mj-variants]');
      this._variants = json ? JSON.parse(json.textContent) : [];
    }
    return this._variants;
  }

  get #colorIndex() {
    return this.dataset.colorIndex === undefined ? -1 : Number(this.dataset.colorIndex);
  }

  #onClick = (event) => {
    const swatch = event.target.closest('.mj-card__swatch');
    if (swatch && this.contains(swatch)) return this.#selectSwatch(swatch);

    if (event.target.closest('[data-mj-quick-toggle]')) return this.#toggleQuickAdd();

    const option = event.target.closest('[data-mj-variant-id]');
    if (option && this.contains(option)) return this.#addToCart(Number(option.dataset.mjVariantId), option);
  };

  #selectSwatch(swatch) {
    this.querySelectorAll('.mj-card__swatch').forEach((el) => el.setAttribute('aria-pressed', String(el === swatch)));
    this.selectedColor = swatch.dataset.value;

    const image = this.querySelector('.mj-card__image--primary');
    if (image && swatch.dataset.src) {
      image.srcset = swatch.dataset.srcset || '';
      image.src = swatch.dataset.src;
    }
    if (this.strip) this.strip.scrollTo({ left: 0 });

    if (swatch.dataset.url) {
      this.querySelectorAll('[data-mj-link]').forEach((link) => {
        link.href = swatch.dataset.url;
      });
    }

    this.#closeQuickAdd();
  }

  #candidateVariants() {
    const available = this.#variants.filter((variant) => variant.available);
    const colorIndex = this.#colorIndex;
    if (colorIndex < 0) return available;

    const color = this.selectedColor ?? available[0]?.options[colorIndex];
    return available.filter((variant) => variant.options[colorIndex] === color);
  }

  #toggleQuickAdd() {
    const panel = this.querySelector('[data-mj-quick-panel]');
    if (!panel) return;
    if (!panel.hidden) return this.#closeQuickAdd();

    const candidates = this.#candidateVariants();
    if (candidates.length === 1) return this.#addToCart(candidates[0].id, this.querySelector('[data-mj-quick-toggle]'));

    const colorIndex = this.#colorIndex;
    const options = this.querySelector('[data-mj-quick-options]');
    options.replaceChildren(
      ...candidates.map((variant) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mj-card__quick-option';
        button.dataset.mjVariantId = String(variant.id);
        const label = variant.options.filter((_, index) => index !== colorIndex).join(' / ');
        button.textContent = label || variant.title;
        return button;
      })
    );
    this.querySelector('[data-mj-quick-message]').textContent = '';
    panel.hidden = false;
    this.querySelector('[data-mj-quick-toggle]').setAttribute('aria-expanded', 'true');
    options.querySelector('button')?.focus();
  }

  #closeQuickAdd() {
    const panel = this.querySelector('[data-mj-quick-panel]');
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    this.querySelector('[data-mj-quick-toggle]')?.setAttribute('aria-expanded', 'false');
  }

  async #addToCart(variantId, trigger) {
    if (this.busy) return;
    this.busy = true;
    trigger?.setAttribute('aria-busy', 'true');

    const message = this.querySelector('[data-mj-quick-message]');
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
      }).then((res) => res.json());

      const cart = await fetch(`${root}cart.js`).then((res) => res.json());
      const didError = Boolean(response.status);

      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        if (message) message.textContent = response.description || response.message || '';
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mj-product-card',
          sourceId: this.dataset.productId,
          itemCount: 1,
          productId: this.dataset.productId,
          sections: response.sections,
          didError,
        },
      });

      if (!didError) {
        this.#closeQuickAdd();
        const toggle = this.querySelector('[data-mj-quick-toggle]');
        toggle?.classList.add('is-added');
        toggle?.setAttribute('aria-label', ADDED_TEXT());
        setTimeout(() => toggle?.classList.remove('is-added'), 2000);
      }
    } catch (error) {
      deferred.reject(error);
      if (message) message.textContent = window.Theme?.translations?.add_to_cart_error || 'Une erreur est survenue.';
    } finally {
      this.busy = false;
      trigger?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('mj-product-card')) {
  customElements.define('mj-product-card', MjProductCard);
}
